import { Grid, Text, Stack, Input } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'utils/dates';
import { renderGenderLabel, renderYesNoValue } from 'utils/general';
import { PaperHeader } from 'components/PaperHeader/PaperHeader';
import { PatientIdProps } from 'models/general';
import { useProfile, useUpdateCustomField } from 'hooks/api/useProfile';
import { CenteredLoading } from 'components/CenteredLoading';
import { usePatientDetailContext } from 'context/hooks';
import { displayMeasurementHeightUnitsLabel, displayMeasurementWeightUnitsLabel } from 'utils/helpers';
import { showWeightFeature } from 'utils/app';
import { DebouncedInput } from 'components/ControlledInputs/DebouncedInput';
import { ProfileCustomFields } from 'models/profile';

export const PatientHealthInfo = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const { unitSystem } = usePatientDetailContext();
  const { data: patientProfile, isLoading } = useProfile(patientId);

  const { mutate: changePhysician } = useUpdateCustomField({
    customField: ProfileCustomFields.Physician,
    fieldLabel: t('label_physician'),
  });

  if (isLoading) return <CenteredLoading />;

  return (
    <>
      <PaperHeader titleKey={'patients_health_info'} iconKey={'patientdetails_personalhealthinfo_healthinfo_tooltip'} />
      <Stack mt={20}>
        <Grid align={'center'}>
          <Grid.Col span={5}>
            <Text>{t('patients_group_birthdate_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input
              defaultValue={
                patientProfile?.birthday ? formatDate({ timestamp: new Date(patientProfile?.birthday) }) : '-'
              }
              name={'birthdate'}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('patients_group_sex_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Input defaultValue={renderGenderLabel(patientProfile?.gender)} name={'sex'} disabled />
          </Grid.Col>
          {showWeightFeature && (
            <>
              <Grid.Col span={5}>
                <Text>{t('patients_group_height_label')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Input
                  value={displayMeasurementHeightUnitsLabel({ height: patientProfile?.length, unitSystem })}
                  name={'height'}
                  disabled
                />
              </Grid.Col>
              <Grid.Col span={5}>
                <Text>{t('patients_group_weight_label')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Input
                  value={displayMeasurementWeightUnitsLabel({ weight: patientProfile?.weight, unitSystem })}
                  name={'weight'}
                  disabled
                />
              </Grid.Col>
              <Grid.Col span={5}>
                <Text>{t('patients_group_af_history_label')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Input defaultValue={renderYesNoValue(patientProfile?.afHistory)} name={'af_history'} disabled />
              </Grid.Col>
              <Grid.Col span={5}>
                <Text>{t('patients_group_smoker_label')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Input defaultValue={renderYesNoValue(patientProfile?.smoker)} name={'smoker'} disabled />
              </Grid.Col>
              <Grid.Col span={5}>
                <Text>{t('label_physician')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <DebouncedInput
                  onChange={(value) => changePhysician({ profileId: patientProfile?.id, value })}
                  value={patientProfile?.customFields?.extraPhysician}
                />
              </Grid.Col>
            </>
          )}
        </Grid>
      </Stack>
    </>
  );
};
